import { IconCheckCircleFilled, IconChevronRight } from "@/assets/icons";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { SuggestedTaskCard } from "@/features/study/components/SuggestedTaskCard";
import { TStudySubject, useStudy } from "@/infrastructure/adapters/study";
import { background, black, divider, primary, text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded, spacing } from "@/theme/variables";
import { Box, Stack, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export const StudyPage: FC = () => {
  const { t } = useTranslation();
  const { subjects, recommendation, isLoading } = useStudy();

  if (isLoading) return <LoadingArea />;

  return (
    <Stack flex={1} gap={2.5}>
      {/* Recommended subject card */}
      <SuggestedTaskCard recommendation={recommendation} />

      {/* Subject list */}
      <Stack gap={2}>
        <Typography sx={{ ...typography.sub16Bold, color: text.primary }}>{t("study.subjectList")}</Typography>
        <Stack gap={2}>
          {subjects.map((subject) => (
            <SubjectItem key={subject.id} subject={subject} />
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

/** Single subject list item */
const SubjectItem: FC<{ subject: TStudySubject }> = ({ subject }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (subject.isLocked) return;
    if (subject.hasLevelAssessment || subject.hasAnswered) {
      navigate(`/study/${subject.id}/contents`);
    } else {
      navigate(`/study/${subject.id}`);
    }
  };

  const isCompleted = subject.isCompleted;
  const isLocked = subject.isLocked;

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: "flex",
        alignItems: "center",
        minHeight: 48,
        px: spacing.sm,
        gap: 1,
        borderRadius: rounded.xs,
        border: `1px solid ${isCompleted && !isLocked ? primary[400] : divider.high}`,
        bgcolor: isLocked ? background.disable : isCompleted ? primary[900] : background.white,
        cursor: isLocked ? "default" : "pointer",
        opacity: isLocked ? 0.5 : 1,
        pointerEvents: isLocked ? "none" : "auto",
      }}
    >
      {/* Lock icon for locked subjects */}
      {isLocked && (
        <Box sx={{ display: "flex", fontSize: 20, color: text.disable, flexShrink: 0 }}>
          <LockOutlinedIcon fontSize="inherit" />
        </Box>
      )}
      {/* Completion icon */}
      {!isLocked && isCompleted && (
        <Box sx={{ display: "flex", fontSize: 24, color: primary[500], flexShrink: 0 }}>
          <IconCheckCircleFilled />
        </Box>
      )}
      {/* Subject name */}
      <Typography
        sx={{
          ...typography.body14Semi,
          color: text.primary,
          flex: 1,
          whiteSpace: "normal",
          wordBreak: "break-word",
        }}
      >
        {subject.name}
      </Typography>
      {/* Chevron */}
      {!isLocked && (
        <Box sx={{ display: "flex", fontSize: 20, flexShrink: 0 }}>
          <IconChevronRight color={black[90]} />
        </Box>
      )}
    </Box>
  );
};

export default StudyPage;
